import React, { useState, useMemo } from 'react';
import {
  Search,
  Bell,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Layers,
  Lock,
  Network,
  FileText,
  RefreshCw,
  Users,
  TrendingUp,
  DollarSign,
  Database,
  ShieldAlert,
  Sliders,
  HelpCircle,
  ChevronRight,
  Plus,
  ShieldCheck,
  Check,
  ExternalLink,
  Clock,
  Filter,
  X,
  Building2,
  Globe,
  Key,
  CreditCard,
  LifeBuoy,
  History,
  FolderGit2,
  Zap,
  MoreHorizontal,
  Download,
  Mail,
  ChevronDown,
  UserPlus,
  Trash2,
  Power,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Radio,
  SlidersHorizontal,
  ChevronLeft
} from 'lucide-react';
import { AppView } from '../../types';

interface OrganizationsManagementProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type OrgType = 'Enterprise' | 'Developer Platform' | 'Scale Workspace' | 'Team / Startup';
export type OrgStatus = 'Active' | 'Suspended' | 'Pending' | 'Provisioning';
export type OrgPlan = 'Enterprise SLA' | 'Scale Plan' | 'Growth Plan' | 'Free Developer';
export type PrimaryMarket = 'Global' | 'US' | 'EU (Eurozone)' | 'UK (GBP)' | 'APAC' | 'LATAM';

export interface OrgMember {
  id: string;
  name: string;
  email: string;
  role: 'Org Owner' | 'Platform Admin' | 'Lead Developer' | 'Developer' | 'Billing Contact' | 'Viewer';
  status: 'Active' | 'Invited' | 'Suspended';
  mfaEnabled: boolean;
  lastActive: string;
}

export interface OrgApiKey {
  id: string;
  name: string;
  maskedKey: string;
  environment: 'Production' | 'Staging' | 'Sandbox';
  rateLimit: string;
  createdDate: string;
  lastUsed: string;
  status: 'Active' | 'Revoked';
  scopes: string[];
}

export interface OrgProject {
  id: string;
  name: string;
  environment: 'Production' | 'Staging' | 'Sandbox';
  services: string[];
  webhookUrl: string;
  webhookStatus: 'Healthy' | 'Degraded' | 'Disabled';
  monthlyCalls: number;
}

export interface OrgTicket {
  id: string;
  title: string;
  priority: 'P1 - Urgent' | 'P2 - High' | 'P3 - Normal';
  status: 'Open' | 'In Progress' | 'Resolved';
  createdDate: string;
  lastUpdated: string;
}

export interface OrgAuditLog {
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  target: string;
  ipAddress: string;
}

export interface Organization {
  id: string;
  name: string;
  domain: string;
  type: OrgType;
  primaryMarket: PrimaryMarket;
  membersCount: number;
  plan: OrgPlan;
  status: OrgStatus;
  createdDate: string;
  primaryAdmin: {
    name: string;
    email: string;
    avatarInitials: string;
  };
  lastActivity: string;
  parentOrgId?: string;
  childDivisions?: string[];
  entitlements: {
    monthlyApiQuota: number;
    monthlyApiUsed: number;
    calculationConcurrencyLimit: number;
    customTaxFormulasEnabled: boolean;
    dedicatedSreSla: boolean;
    webhookRetentionHours: number;
  };
  billing: {
    planName: string;
    monthlyCost: number;
    billingCycle: 'Annual' | 'Monthly';
    invoiceEmail: string;
    nextInvoiceDate: string;
    paymentMethod: string;
    status: 'Current' | 'Overdue' | 'Exempt';
  };
  support: {
    tamName: string;
    tamEmail: string;
    slaResponseTime: string;
  };
  members: OrgMember[];
  apiKeys: OrgApiKey[];
  projects: OrgProject[];
  tickets: OrgTicket[];
  auditLogs: OrgAuditLog[];
}

export const OrganizationsManagement: React.FC<OrganizationsManagementProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('customers');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPlanFilter, setSelectedPlanFilter] = useState<string>('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL');
  const [selectedMarketFilter, setSelectedMarketFilter] = useState<string>('ALL');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('ALL');

  // Selected row checkboxes
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  
  // Drawer / Details Modal State
  const [activeOrgDetail, setActiveOrgDetail] = useState<Organization | null>(null);
  const [detailTab, setDetailTab] = useState<'overview' | 'members' | 'projects' | 'apikeys' | 'entitlements' | 'support' | 'billing' | 'audit'>('overview');

  // Add Org Modal
  const [showAddOrgModal, setShowAddOrgModal] = useState<boolean>(false);
  const [newOrgName, setNewOrgName] = useState<string>('');
  const [newOrgDomain, setNewOrgDomain] = useState<string>('');
  const [newOrgType, setNewOrgType] = useState<OrgType>('Enterprise');
  const [newOrgMarket, setNewOrgMarket] = useState<PrimaryMarket>('Global');
  const [newOrgPlan, setNewOrgPlan] = useState<OrgPlan>('Enterprise SLA');
  const [newOrgAdminName, setNewOrgAdminName] = useState<string>('');
  const [newOrgAdminEmail, setNewOrgAdminEmail] = useState<string>('');

  // Initial Real Data for Organizations
  const [organizations, setOrganizations] = useState<Organization[]>([
    {
      id: 'org_acme_89f',
      name: 'Acme Corp Global',
      domain: 'acme.com',
      type: 'Enterprise',
      primaryMarket: 'Global',
      membersCount: 24,
      plan: 'Enterprise SLA',
      status: 'Active',
      createdDate: '2025-10-15',
      primaryAdmin: {
        name: 'Sarah Jenkins',
        email: 'sarah@acme.com',
        avatarInitials: 'SJ'
      },
      lastActivity: '4 mins ago',
      parentOrgId: undefined,
      childDivisions: ['Acme EMEA Financial', 'Acme US Technology'],
      entitlements: {
        monthlyApiQuota: 10000000,
        monthlyApiUsed: 6840200,
        calculationConcurrencyLimit: 100,
        customTaxFormulasEnabled: true,
        dedicatedSreSla: true,
        webhookRetentionHours: 72
      },
      billing: {
        planName: 'Enterprise Platform License',
        monthlyCost: 4500,
        billingCycle: 'Annual',
        invoiceEmail: 'billing@acme.com',
        nextInvoiceDate: '2026-11-15',
        paymentMethod: 'Direct Corporate Wire (ACH / SEPA)',
        status: 'Current'
      },
      support: {
        tamName: 'Alexandre Mercier',
        tamEmail: 'alexandre.m@yamoopay.com',
        slaResponseTime: '< 15 mins (24/7 P1)'
      },
      members: [
        { id: 'm-1', name: 'Sarah Jenkins', email: 'sarah@acme.com', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: '4 mins ago' },
        { id: 'm-2', name: 'David Vance', email: 'david.v@acme.com', role: 'Platform Admin', status: 'Active', mfaEnabled: true, lastActive: '1 hour ago' },
        { id: 'm-3', name: 'Elena Rostova', email: 'elena.r@acme.com', role: 'Lead Developer', status: 'Active', mfaEnabled: true, lastActive: 'Yesterday' },
        { id: 'm-4', name: 'Markus Weber', email: 'markus.w@acme.com', role: 'Billing Contact', status: 'Active', mfaEnabled: true, lastActive: '3 days ago' }
      ],
      apiKeys: [
        { id: 'k-1', name: 'Production Core Engine Live', maskedKey: 'yp_live_9a8f••••••••••••34d1', environment: 'Production', rateLimit: '5,000 req/min', createdDate: '2026-01-12', lastUsed: '4 mins ago', status: 'Active', scopes: ['salary:*', 'fx:live', 'loans:compute', 'vat:calculate'] },
        { id: 'k-2', name: 'Staging Integration Key', maskedKey: 'yp_test_3b1c••••••••••••89e2', environment: 'Staging', rateLimit: '1,000 req/min', createdDate: '2026-02-01', lastUsed: '2 hours ago', status: 'Active', scopes: ['salary:read', 'fx:live', 'loans:compute'] }
      ],
      projects: [
        { id: 'p-1', name: 'Global Payroll Sync Pipeline', environment: 'Production', services: ['Salary Engine', 'FX Rates', 'VAT Engine'], webhookUrl: 'https://api.acme.com/webhooks/payroll', webhookStatus: 'Healthy', monthlyCalls: 5400000 },
        { id: 'p-2', name: 'Treasury & FX Optimization Tool', environment: 'Production', services: ['FX Live Rates', 'Currency Intelligence'], webhookUrl: 'https://treasury.acme.com/events', webhookStatus: 'Healthy', monthlyCalls: 1440200 }
      ],
      tickets: [
        { id: 'TKT-8902', title: 'Request for custom Eurozone FX spread parameter formula', priority: 'P2 - High', status: 'Resolved', createdDate: '2026-08-10', lastUpdated: '2026-08-14' },
        { id: 'TKT-9140', title: 'High concurrency webhook delivery throughput inquiry', priority: 'P2 - High', status: 'In Progress', createdDate: '2026-08-26', lastUpdated: '2026-08-27' }
      ],
      auditLogs: [
        { id: 'a-1', timestamp: '2026-08-27 16:30:12', actor: 'SuperAdmin (Control Center)', action: 'Adjusted calculation concurrency to 100 workers', target: 'org_acme_89f', ipAddress: '192.168.1.45' },
        { id: 'a-2', timestamp: '2026-08-25 11:20:00', actor: 'sarah@acme.com', action: 'Created new API key: Staging Integration Key', target: 'api_key_3b1c', ipAddress: '142.250.190.46' }
      ]
    },
    {
      id: 'org_nexa_12a',
      name: 'Nexa Tech Solutions',
      domain: 'nexatech.io',
      type: 'Developer Platform',
      primaryMarket: 'EU (Eurozone)',
      membersCount: 12,
      plan: 'Scale Plan',
      status: 'Suspended',
      createdDate: '2025-11-02',
      primaryAdmin: {
        name: 'Marcus Rogers',
        email: 'm.rogers@nexatech.io',
        avatarInitials: 'MR'
      },
      lastActivity: '3 days ago',
      entitlements: {
        monthlyApiQuota: 2500000,
        monthlyApiUsed: 2490100,
        calculationConcurrencyLimit: 30,
        customTaxFormulasEnabled: false,
        dedicatedSreSla: false,
        webhookRetentionHours: 24
      },
      billing: {
        planName: 'Scale Platform Subscription',
        monthlyCost: 890,
        billingCycle: 'Monthly',
        invoiceEmail: 'finance@nexatech.io',
        nextInvoiceDate: '2026-09-02',
        paymentMethod: 'Credit Card (SEPA Visa •••• 4129)',
        status: 'Overdue'
      },
      support: {
        tamName: 'Shared Tier Support',
        tamEmail: 'developer-support@yamoopay.com',
        slaResponseTime: '< 4 hours (Business hours)'
      },
      members: [
        { id: 'm-21', name: 'Marcus Rogers', email: 'm.rogers@nexatech.io', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: '3 days ago' },
        { id: 'm-22', name: 'Lucia Ferri', email: 'l.ferri@nexatech.io', role: 'Developer', status: 'Active', mfaEnabled: false, lastActive: '5 days ago' }
      ],
      apiKeys: [
        { id: 'k-21', name: 'Nexa Gateway Production', maskedKey: 'yp_live_891e••••••••••••901f', environment: 'Production', rateLimit: '2,000 req/min', createdDate: '2025-11-05', lastUsed: '3 days ago', status: 'Active', scopes: ['salary:*', 'vat:calculate'] }
      ],
      projects: [
        { id: 'p-21', name: 'Nexa HR SaaS Integration', environment: 'Production', services: ['Salary Engine', 'VAT Engine'], webhookUrl: 'https://nexatech.io/webhooks/salary', webhookStatus: 'Degraded', monthlyCalls: 2490100 }
      ],
      tickets: [
        { id: 'TKT-9012', title: 'Payment method update and invoice reconciliation', priority: 'P3 - Normal', status: 'Open', createdDate: '2026-08-25', lastUpdated: '2026-08-26' }
      ],
      auditLogs: [
        { id: 'a-21', timestamp: '2026-08-25 09:12:00', actor: 'Billing Automated Engine', action: 'Flagged organization as Suspended (Payment Overdue)', target: 'org_nexa_12a', ipAddress: 'Internal System' }
      ]
    },
    {
      id: 'org_summit_44c',
      name: 'Summit Retail Platform',
      domain: 'summit-retail.net',
      type: 'Scale Workspace',
      primaryMarket: 'US',
      membersCount: 8,
      plan: 'Growth Plan',
      status: 'Pending',
      createdDate: '2026-01-18',
      primaryAdmin: {
        name: 'Kevin Lee',
        email: 'k.lee@summit.net',
        avatarInitials: 'KL'
      },
      lastActivity: '12 hours ago',
      entitlements: {
        monthlyApiQuota: 1000000,
        monthlyApiUsed: 120400,
        calculationConcurrencyLimit: 20,
        customTaxFormulasEnabled: false,
        dedicatedSreSla: false,
        webhookRetentionHours: 24
      },
      billing: {
        planName: 'Growth Workspace Plan',
        monthlyCost: 350,
        billingCycle: 'Monthly',
        invoiceEmail: 'accounts@summit.net',
        nextInvoiceDate: '2026-09-18',
        paymentMethod: 'Corporate Card (Amex •••• 1004)',
        status: 'Current'
      },
      support: {
        tamName: 'Shared Tier Support',
        tamEmail: 'developer-support@yamoopay.com',
        slaResponseTime: '< 8 hours'
      },
      members: [
        { id: 'm-31', name: 'Kevin Lee', email: 'k.lee@summit.net', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: '12 hours ago' },
        { id: 'm-32', name: 'Rachel Adams', email: 'r.adams@summit.net', role: 'Developer', status: 'Invited', mfaEnabled: false, lastActive: 'Never' }
      ],
      apiKeys: [
        { id: 'k-31', name: 'Summit Sandbox Test Key', maskedKey: 'yp_test_44d1••••••••••••77ab', environment: 'Sandbox', rateLimit: '500 req/min', createdDate: '2026-01-19', lastUsed: '12 hours ago', status: 'Active', scopes: ['loans:compute', 'fx:live'] }
      ],
      projects: [
        { id: 'p-31', name: 'Point-of-Sale Loan Estimator', environment: 'Sandbox', services: ['Loan Engine', 'Discount Engine'], webhookUrl: 'https://sandbox.summit.net/webhooks', webhookStatus: 'Healthy', monthlyCalls: 120400 }
      ],
      tickets: [],
      auditLogs: [
        { id: 'a-31', timestamp: '2026-01-18 14:00:00', actor: 'Self-Service Onboarding', action: 'Registered organization profile pending workspace verification', target: 'org_summit_44c', ipAddress: '73.189.44.12' }
      ]
    },
    {
      id: 'org_finscale_99b',
      name: 'FinScale United Kingdom',
      domain: 'finscale.co.uk',
      type: 'Enterprise',
      primaryMarket: 'UK (GBP)',
      membersCount: 38,
      plan: 'Enterprise SLA',
      status: 'Active',
      createdDate: '2025-08-20',
      primaryAdmin: {
        name: 'Oliver Sterling',
        email: 'oliver@finscale.co.uk',
        avatarInitials: 'OS'
      },
      lastActivity: 'Just now',
      parentOrgId: undefined,
      childDivisions: ['FinScale London Treasury', 'FinScale Edinburgh Dev Hub'],
      entitlements: {
        monthlyApiQuota: 25000000,
        monthlyApiUsed: 18450000,
        calculationConcurrencyLimit: 250,
        customTaxFormulasEnabled: true,
        dedicatedSreSla: true,
        webhookRetentionHours: 168
      },
      billing: {
        planName: 'Enterprise Custom Tier',
        monthlyCost: 9200,
        billingCycle: 'Annual',
        invoiceEmail: 'procurement@finscale.co.uk',
        nextInvoiceDate: '2026-08-20',
        paymentMethod: 'BACS / Direct Corporate Transfer',
        status: 'Current'
      },
      support: {
        tamName: 'Claire Thornton (Principal TAM)',
        tamEmail: 'claire.t@yamoopay.com',
        slaResponseTime: '< 15 mins (Dedicated Hotline)'
      },
      members: [
        { id: 'm-41', name: 'Oliver Sterling', email: 'oliver@finscale.co.uk', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: 'Just now' },
        { id: 'm-42', name: 'Gareth Edwards', email: 'g.edwards@finscale.co.uk', role: 'Platform Admin', status: 'Active', mfaEnabled: true, lastActive: '20 mins ago' },
        { id: 'm-43', name: 'Priya Patel', email: 'priya.p@finscale.co.uk', role: 'Lead Developer', status: 'Active', mfaEnabled: true, lastActive: '1 hour ago' }
      ],
      apiKeys: [
        { id: 'k-41', name: 'FinScale Production Core Key', maskedKey: 'yp_live_11aa••••••••••••9944', environment: 'Production', rateLimit: '10,000 req/min', createdDate: '2025-08-22', lastUsed: 'Just now', status: 'Active', scopes: ['salary:*', 'fx:*', 'loans:*', 'vat:*', 'analytics:*'] }
      ],
      projects: [
        { id: 'p-41', name: 'FinScale UK Banking Aggregator', environment: 'Production', services: ['Salary Engine', 'FX Rates', 'Loan Engine', 'VAT Engine'], webhookUrl: 'https://core.finscale.co.uk/yamoopay-events', webhookStatus: 'Healthy', monthlyCalls: 18450000 }
      ],
      tickets: [
        { id: 'TKT-9100', title: 'Q3 Enterprise SLA Quarterly Architecture Review', priority: 'P3 - Normal', status: 'Resolved', createdDate: '2026-08-01', lastUpdated: '2026-08-15' }
      ],
      auditLogs: [
        { id: 'a-41', timestamp: '2026-08-28 08:14:00', actor: 'oliver@finscale.co.uk', action: 'Downloaded annual platform compliance attestation (SOC 2 / ISO 27001)', target: 'compliance_report_2026', ipAddress: '82.165.197.1' }
      ]
    },
    {
      id: 'org_stellarpay_77e',
      name: 'StellarPay API Labs',
      domain: 'stellarpay.dev',
      type: 'Developer Platform',
      primaryMarket: 'Global',
      membersCount: 5,
      plan: 'Free Developer',
      status: 'Active',
      createdDate: '2026-04-10',
      primaryAdmin: {
        name: 'Tara Lin',
        email: 'tara@stellarpay.dev',
        avatarInitials: 'TL'
      },
      lastActivity: '2 hours ago',
      entitlements: {
        monthlyApiQuota: 50000,
        monthlyApiUsed: 31200,
        calculationConcurrencyLimit: 5,
        customTaxFormulasEnabled: false,
        dedicatedSreSla: false,
        webhookRetentionHours: 12
      },
      billing: {
        planName: 'Free Developer Community Plan',
        monthlyCost: 0,
        billingCycle: 'Monthly',
        invoiceEmail: 'tara@stellarpay.dev',
        nextInvoiceDate: 'N/A (Free Tier)',
        paymentMethod: 'None Required',
        status: 'Exempt'
      },
      support: {
        tamName: 'Community Forum / Discord',
        tamEmail: 'dev-community@yamoopay.com',
        slaResponseTime: 'Best Effort'
      },
      members: [
        { id: 'm-51', name: 'Tara Lin', email: 'tara@stellarpay.dev', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: '2 hours ago' }
      ],
      apiKeys: [
        { id: 'k-51', name: 'StellarPay Community Sandbox', maskedKey: 'yp_test_99ab••••••••••••12aa', environment: 'Sandbox', rateLimit: '100 req/min', createdDate: '2026-04-10', lastUsed: '2 hours ago', status: 'Active', scopes: ['fx:live', 'salary:read'] }
      ],
      projects: [
        { id: 'p-51', name: 'Open Source Remittance Widget', environment: 'Sandbox', services: ['FX Rates'], webhookUrl: 'https://stellarpay.dev/webhooks', webhookStatus: 'Healthy', monthlyCalls: 31200 }
      ],
      tickets: [],
      auditLogs: []
    },
    {
      id: 'org_horizon_33a',
      name: 'Horizon Multi-Cloud Enterprise',
      domain: 'horizon.eu',
      type: 'Enterprise',
      primaryMarket: 'EU (Eurozone)',
      membersCount: 42,
      plan: 'Enterprise SLA',
      status: 'Active',
      createdDate: '2025-06-11',
      primaryAdmin: {
        name: 'Jean-Paul Moreau',
        email: 'jp.moreau@horizon.eu',
        avatarInitials: 'JM'
      },
      lastActivity: '18 mins ago',
      parentOrgId: undefined,
      childDivisions: ['Horizon Paris Hub', 'Horizon Frankfurt Cloud'],
      entitlements: {
        monthlyApiQuota: 15000000,
        monthlyApiUsed: 11200400,
        calculationConcurrencyLimit: 150,
        customTaxFormulasEnabled: true,
        dedicatedSreSla: true,
        webhookRetentionHours: 120
      },
      billing: {
        planName: 'Enterprise Platform Tier 2',
        monthlyCost: 6200,
        billingCycle: 'Annual',
        invoiceEmail: 'comptabilite@horizon.eu',
        nextInvoiceDate: '2026-06-11',
        paymentMethod: 'SEPA Direct Debit Corporate',
        status: 'Current'
      },
      support: {
        tamName: 'Alexandre Mercier',
        tamEmail: 'alexandre.m@yamoopay.com',
        slaResponseTime: '< 15 mins (24/7)'
      },
      members: [
        { id: 'm-61', name: 'Jean-Paul Moreau', email: 'jp.moreau@horizon.eu', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: '18 mins ago' },
        { id: 'm-62', name: 'Chloe Dupont', email: 'c.dupont@horizon.eu', role: 'Platform Admin', status: 'Active', mfaEnabled: true, lastActive: '1 hour ago' }
      ],
      apiKeys: [
        { id: 'k-61', name: 'Horizon Cloud Core Key', maskedKey: 'yp_live_44ee••••••••••••8811', environment: 'Production', rateLimit: '6,000 req/min', createdDate: '2025-06-15', lastUsed: '18 mins ago', status: 'Active', scopes: ['salary:*', 'fx:*', 'vat:*'] }
      ],
      projects: [
        { id: 'p-61', name: 'EU Cross-Border Compensation Hub', environment: 'Production', services: ['Salary Engine', 'VAT Engine', 'FX Rates'], webhookUrl: 'https://api.horizon.eu/yamoopay/events', webhookStatus: 'Healthy', monthlyCalls: 11200400 }
      ],
      tickets: [],
      auditLogs: []
    },
    {
      id: 'org_alphawave_61k',
      name: 'AlphaWave Systems',
      domain: 'alphawave.io',
      type: 'Team / Startup',
      primaryMarket: 'APAC',
      membersCount: 6,
      plan: 'Growth Plan',
      status: 'Provisioning',
      createdDate: '2026-08-27',
      primaryAdmin: {
        name: 'Kenji Sato',
        email: 'kenji@alphawave.io',
        avatarInitials: 'KS'
      },
      lastActivity: '30 mins ago',
      entitlements: {
        monthlyApiQuota: 1000000,
        monthlyApiUsed: 4200,
        calculationConcurrencyLimit: 20,
        customTaxFormulasEnabled: false,
        dedicatedSreSla: false,
        webhookRetentionHours: 24
      },
      billing: {
        planName: 'Growth Workspace Plan',
        monthlyCost: 350,
        billingCycle: 'Monthly',
        invoiceEmail: 'billing@alphawave.io',
        nextInvoiceDate: '2026-09-27',
        paymentMethod: 'Corporate Card (Mastercard •••• 8831)',
        status: 'Current'
      },
      support: {
        tamName: 'Shared Tier Support',
        tamEmail: 'developer-support@yamoopay.com',
        slaResponseTime: '< 8 hours'
      },
      members: [
        { id: 'm-71', name: 'Kenji Sato', email: 'kenji@alphawave.io', role: 'Org Owner', status: 'Active', mfaEnabled: true, lastActive: '30 mins ago' }
      ],
      apiKeys: [
        { id: 'k-71', name: 'APAC Ingestion Sandbox', maskedKey: 'yp_test_61aa••••••••••••5502', environment: 'Sandbox', rateLimit: '500 req/min', createdDate: '2026-08-27', lastUsed: '30 mins ago', status: 'Active', scopes: ['fx:live', 'loans:compute'] }
      ],
      projects: [
        { id: 'p-71', name: 'APAC Multi-Currency Lending Widget', environment: 'Sandbox', services: ['Loan Engine', 'FX Rates'], webhookUrl: 'https://api.alphawave.io/hooks', webhookStatus: 'Healthy', monthlyCalls: 4200 }
      ],
      tickets: [],
      auditLogs: []
    }
  ]);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Summary Metrics
  const totalOrgs = organizations.length;
  const activeOrgs = organizations.filter((o) => o.status === 'Active').length;
  const developerOrgs = organizations.filter((o) => o.type === 'Developer Platform' || o.plan === 'Free Developer').length;
  const enterpriseAccounts = organizations.filter((o) => o.type === 'Enterprise' || o.plan === 'Enterprise SLA').length;
  const pendingInvitations = organizations.filter((o) => o.status === 'Pending' || o.status === 'Provisioning').length;

  // Filtered List
  const filteredOrganizations = useMemo(() => {
    return organizations.filter((org) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = org.name.toLowerCase().includes(q);
        const matchDomain = org.domain.toLowerCase().includes(q);
        const matchAdmin = org.primaryAdmin.email.toLowerCase().includes(q) || org.primaryAdmin.name.toLowerCase().includes(q);
        const matchId = org.id.toLowerCase().includes(q);
        if (!matchName && !matchDomain && !matchAdmin && !matchId) return false;
      }
      // Plan
      if (selectedPlanFilter !== 'ALL' && org.plan !== selectedPlanFilter) {
        return false;
      }
      // Status
      if (selectedStatusFilter !== 'ALL' && org.status !== selectedStatusFilter) {
        return false;
      }
      // Market
      if (selectedMarketFilter !== 'ALL' && org.primaryMarket !== selectedMarketFilter) {
        return false;
      }
      // Type
      if (selectedTypeFilter !== 'ALL' && org.type !== selectedTypeFilter) {
        return false;
      }
      return true;
    });
  }, [organizations, searchQuery, selectedPlanFilter, selectedStatusFilter, selectedMarketFilter, selectedTypeFilter]);

  // Bulk Selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrgIds(filteredOrganizations.map((o) => o.id));
    } else {
      setSelectedOrgIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    if (selectedOrgIds.includes(id)) {
      setSelectedOrgIds(selectedOrgIds.filter((item) => item !== id));
    } else {
      setSelectedOrgIds([...selectedOrgIds, id]);
    }
  };

  // Add Organization Handler
  const handleCreateOrganization = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim() || !newOrgDomain.trim() || !newOrgAdminEmail.trim()) {
      triggerToast('Please fill in all required organization fields.');
      return;
    }

    const newOrg: Organization = {
      id: `org_${newOrgName.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8)}_${Math.floor(100 + Math.random() * 900)}`,
      name: newOrgName.trim(),
      domain: newOrgDomain.trim().toLowerCase(),
      type: newOrgType,
      primaryMarket: newOrgMarket,
      membersCount: 1,
      plan: newOrgPlan,
      status: 'Active',
      createdDate: new Date().toISOString().split('T')[0],
      primaryAdmin: {
        name: newOrgAdminName.trim() || 'Admin Contact',
        email: newOrgAdminEmail.trim().toLowerCase(),
        avatarInitials: (newOrgAdminName.trim() || 'AC').slice(0, 2).toUpperCase()
      },
      lastActivity: 'Just now',
      entitlements: {
        monthlyApiQuota: newOrgPlan === 'Enterprise SLA' ? 10000000 : newOrgPlan === 'Scale Plan' ? 2500000 : 1000000,
        monthlyApiUsed: 0,
        calculationConcurrencyLimit: newOrgPlan === 'Enterprise SLA' ? 100 : 25,
        customTaxFormulasEnabled: newOrgPlan === 'Enterprise SLA',
        dedicatedSreSla: newOrgPlan === 'Enterprise SLA',
        webhookRetentionHours: newOrgPlan === 'Enterprise SLA' ? 72 : 24
      },
      billing: {
        planName: newOrgPlan === 'Enterprise SLA' ? 'Enterprise Platform License' : 'Scale Platform Plan',
        monthlyCost: newOrgPlan === 'Enterprise SLA' ? 4500 : 890,
        billingCycle: 'Annual',
        invoiceEmail: newOrgAdminEmail.trim().toLowerCase(),
        nextInvoiceDate: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().split('T')[0],
        paymentMethod: 'Direct Invoicing',
        status: 'Current'
      },
      support: {
        tamName: newOrgPlan === 'Enterprise SLA' ? 'Alexandre Mercier' : 'Shared Support',
        tamEmail: 'developer-support@yamoopay.com',
        slaResponseTime: newOrgPlan === 'Enterprise SLA' ? '< 15 mins (24/7)' : '< 4 hours'
      },
      members: [
        {
          id: `m-${Date.now()}`,
          name: newOrgAdminName.trim() || 'Primary Admin',
          email: newOrgAdminEmail.trim().toLowerCase(),
          role: 'Org Owner',
          status: 'Active',
          mfaEnabled: true,
          lastActive: 'Just now'
        }
      ],
      apiKeys: [
        {
          id: `k-${Date.now()}`,
          name: 'Primary API Key',
          maskedKey: `yp_live_${Math.random().toString(36).substring(2, 6)}••••••••••••${Math.random().toString(36).substring(2, 6)}`,
          environment: 'Production',
          rateLimit: newOrgPlan === 'Enterprise SLA' ? '5,000 req/min' : '1,000 req/min',
          createdDate: new Date().toISOString().split('T')[0],
          lastUsed: 'Never',
          status: 'Active',
          scopes: ['salary:*', 'fx:*', 'loans:*']
        }
      ],
      projects: [],
      tickets: [],
      auditLogs: [
        {
          id: `a-${Date.now()}`,
          timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
          actor: 'SuperAdmin',
          action: 'Provisioned new client organization',
          target: newOrgName,
          ipAddress: '192.168.1.1'
        }
      ]
    };

    setOrganizations([newOrg, ...organizations]);
    setShowAddOrgModal(false);
    setNewOrgName('');
    setNewOrgDomain('');
    setNewOrgAdminName('');
    setNewOrgAdminEmail('');
    triggerToast(`Organization "${newOrg.name}" successfully created!`);
  };

  // Export handler
  const handleExportList = () => {
    const headers = ['ID', 'Organization Name', 'Domain', 'Type', 'Market', 'Plan', 'Status', 'Members', 'Admin Email', 'Created Date'];
    const rows = filteredOrganizations.map((o) => [
      o.id,
      `"${o.name}"`,
      o.domain,
      `"${o.type}"`,
      `"${o.primaryMarket}"`,
      `"${o.plan}"`,
      o.status,
      o.membersCount,
      o.primaryAdmin.email,
      o.createdDate
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `yamoopay_organizations_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    triggerToast(`Exported ${filteredOrganizations.length} organizations to CSV.`);
  };

  // Quick Action: Toggle Status
  const handleToggleStatus = (orgId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOrganizations((prev) =>
      prev.map((o) => {
        if (o.id === orgId) {
          const nextStatus = o.status === 'Active' ? 'Suspended' : 'Active';
          triggerToast(`Organization ${o.name} status updated to ${nextStatus}`);
          return { ...o, status: nextStatus };
        }
        return o;
      })
    );
    if (activeOrgDetail && activeOrgDetail.id === orgId) {
      setActiveOrgDetail((prev) => (prev ? { ...prev, status: prev.status === 'Active' ? 'Suspended' : 'Active' } : null));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains)                           */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F172A] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Admin Console
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-6">
          {/* Section: CORE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              CORE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('overview');
                  onNavigateToView('admin-super-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'overview'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('customers');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedDomain === 'customers'
                    ? 'bg-[#2563EB] text-white shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Customers & Support</span>
              </button>

              {/* Sub-navigation under Customers & Support */}
              {selectedDomain === 'customers' && (
                <div className="ml-4 pl-3 border-l-2 border-[#2563EB] my-1 space-y-1">
                  <button
                    onClick={() => {}}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#2563EB] bg-blue-50 transition-colors cursor-pointer"
                  >
                    Organizations
                  </button>
                  <button
                    onClick={() => onNavigateToView('cxo-command-center')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    Experience & Support
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setSelectedDomain('product');
                  onNavigateToView('cpo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'product'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Product & Growth</span>
              </button>
            </nav>
          </div>

          {/* Section: PERFORMANCE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              PERFORMANCE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('revenue');
                  onNavigateToView('cfo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'revenue'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>Revenue & Finance</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('data');
                  onNavigateToView('cdo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'data'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Data & AI</span>
              </button>
            </nav>
          </div>

          {/* Section: SYSTEM */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              SYSTEM
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('operations');
                  onNavigateToView('admin-system-health');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'operations'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Operations & Risk</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('configuration');
                  onNavigateToView('admin-roles-permissions');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'configuration'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Sliders className="w-4 h-4" />
                <span>Configuration</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-emerald-400 font-bold text-xs flex items-center justify-center border border-emerald-500/30">
              AU
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Admin User</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">admin@yamoopay.com</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20 shadow-2xs">
          {/* Search Input */}
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations, domains, or admin emails..."
                className="w-full pl-9 pr-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] placeholder-[#94A3B8] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Topbar Utility Icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => triggerToast('Platform security: MFA, OIDC & TLS active across all customer workspaces')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Security Status"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              onClick={() => triggerToast('Notification Center: All customer webhooks operating normally')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigateToView('help-center')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Help & Documentation"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-500 text-white font-bold text-xs flex items-center justify-center shadow-xs">
              AD
            </div>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* Breadcrumbs & Title Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs text-[#64748B] font-medium mb-1.5">
                <Users className="w-3.5 h-3.5 text-[#2563EB]" />
                <span>Customers & Support</span>
                <ChevronRight className="w-3 h-3 text-[#CBD5E1]" />
                <span className="font-bold text-[#0F172A]">Organizations</span>
              </div>
              <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">
                Organizations Directory
              </h1>
              <p className="text-xs text-[#64748B] font-medium mt-1">
                Client organizations and workspace accounts on the YamooPay Developer Platform & Enterprise services.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportList}
                className="px-3.5 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#334155] font-bold text-xs rounded-xl shadow-2xs flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-[#64748B]" />
                <span>Export List</span>
              </button>

              <button
                onClick={() => setShowAddOrgModal(true)}
                className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4 text-white" />
                <span>Add Organization</span>
              </button>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 3. SUMMARY CARDS ROW (4 Cards)                                  */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Active Organizations */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  ACTIVE ORGANIZATIONS
                </span>
                <Building2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                {activeOrgs}
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                {totalOrgs} total client workspaces
              </p>
            </div>

            {/* Card 2: Developer Organizations */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  DEVELOPER ORGS
                </span>
                <Zap className="w-4 h-4 text-[#2563EB]" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                {developerOrgs}
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                API integration & sandbox accounts
              </p>
            </div>

            {/* Card 3: Enterprise Accounts */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  ENTERPRISE ACCOUNTS
                </span>
                <ShieldCheck className="w-4 h-4 text-purple-600" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                {enterpriseAccounts}
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                Dedicated 24/7 SLA contracts
              </p>
            </div>

            {/* Card 4: Pending Invitations & Provisioning */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  PENDING INVITATIONS
                </span>
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                {pendingInvitations}
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                Awaiting workspace onboarding
              </p>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 4. FILTER TOOLBAR & SELECTION ACTIONS                           */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs space-y-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Filter: Plan */}
                <div className="relative">
                  <select
                    value={selectedPlanFilter}
                    onChange={(e) => setSelectedPlanFilter(e.target.value)}
                    className="px-3 py-1.5 pr-8 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs font-bold text-[#334155] focus:outline-none focus:ring-2 focus:ring-[#2563EB] appearance-none cursor-pointer"
                  >
                    <option value="ALL">All Plans</option>
                    <option value="Enterprise SLA">Enterprise SLA</option>
                    <option value="Scale Plan">Scale Plan</option>
                    <option value="Growth Plan">Growth Plan</option>
                    <option value="Free Developer">Free Developer</option>
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 text-[#64748B] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                {/* Filter: Status */}
                <div className="relative">
                  <select
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                    className="px-3 py-1.5 pr-8 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs font-bold text-[#334155] focus:outline-none focus:ring-2 focus:ring-[#2563EB] appearance-none cursor-pointer"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Suspended">Suspended</option>
                    <option value="Pending">Pending</option>
                    <option value="Provisioning">Provisioning</option>
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 text-[#64748B] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                {/* Filter: Market */}
                <div className="relative">
                  <select
                    value={selectedMarketFilter}
                    onChange={(e) => setSelectedMarketFilter(e.target.value)}
                    className="px-3 py-1.5 pr-8 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs font-bold text-[#334155] focus:outline-none focus:ring-2 focus:ring-[#2563EB] appearance-none cursor-pointer"
                  >
                    <option value="ALL">All Markets</option>
                    <option value="Global">Global</option>
                    <option value="US">US</option>
                    <option value="EU (Eurozone)">EU (Eurozone)</option>
                    <option value="UK (GBP)">UK (GBP)</option>
                    <option value="APAC">APAC</option>
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 text-[#64748B] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                {/* Reset Filters */}
                {(selectedPlanFilter !== 'ALL' || selectedStatusFilter !== 'ALL' || selectedMarketFilter !== 'ALL' || searchQuery) && (
                  <button
                    onClick={() => {
                      setSelectedPlanFilter('ALL');
                      setSelectedStatusFilter('ALL');
                      setSelectedMarketFilter('ALL');
                      setSearchQuery('');
                    }}
                    className="px-2.5 py-1.5 text-xs text-[#2563EB] font-bold hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset</span>
                  </button>
                )}
              </div>

              <div className="text-xs text-[#64748B] font-medium">
                Showing <strong className="text-[#0F172A] font-bold">{filteredOrganizations.length}</strong> of{' '}
                <strong className="text-[#0F172A] font-bold">{totalOrgs}</strong> organizations
              </div>
            </div>

            {/* Bulk Action Bar if items selected */}
            {selectedOrgIds.length > 0 && (
              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between text-xs animate-in fade-in duration-150">
                <div className="flex items-center gap-2 text-blue-900 font-bold">
                  <CheckCircle className="w-4 h-4 text-[#2563EB]" />
                  <span>{selectedOrgIds.length} organization(s) selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      triggerToast(`Batch status toggle applied to ${selectedOrgIds.length} organizations.`);
                      setSelectedOrgIds([]);
                    }}
                    className="px-3 py-1 bg-white border border-blue-300 text-blue-900 font-bold rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
                  >
                    Toggle Status
                  </button>
                  <button
                    onClick={() => {
                      triggerToast(`Compliance audit report requested for ${selectedOrgIds.length} organizations.`);
                      setSelectedOrgIds([]);
                    }}
                    className="px-3 py-1 bg-[#0F172A] text-white font-bold rounded-lg hover:bg-[#1E293B] transition-colors cursor-pointer"
                  >
                    Request Audit
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* =============================================================== */}
          {/* 5. ORGANIZATIONS DIRECTORY TABLE                                */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] bg-[#F8FAFC]/50 text-[#64748B]">
                    <th className="py-3.5 pl-4 pr-2 w-8">
                      <input
                        type="checkbox"
                        checked={filteredOrganizations.length > 0 && selectedOrgIds.length === filteredOrganizations.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-[#CBD5E1] text-[#2563EB] focus:ring-0 cursor-pointer"
                      />
                    </th>
                    <th className="py-3.5 px-3 font-semibold">Organization Name</th>
                    <th className="py-3.5 px-3 font-semibold">Type</th>
                    <th className="py-3.5 px-3 font-semibold">Primary Market</th>
                    <th className="py-3.5 px-3 font-semibold">Members</th>
                    <th className="py-3.5 px-3 font-semibold">API Plan / Entitlement</th>
                    <th className="py-3.5 px-3 font-semibold">Status</th>
                    <th className="py-3.5 px-3 font-semibold">Created Date</th>
                    <th className="py-3.5 px-3 font-semibold">Primary Admin</th>
                    <th className="py-3.5 pr-4 pl-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {filteredOrganizations.map((org) => {
                    const isSelected = selectedOrgIds.includes(org.id);
                    return (
                      <tr
                        key={org.id}
                        onClick={() => setActiveOrgDetail(org)}
                        className={`hover:bg-[#F8FAFC] transition-colors cursor-pointer ${
                          isSelected ? 'bg-blue-50/40' : ''
                        }`}
                      >
                        <td className="py-3.5 pl-4 pr-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectOne(org.id)}
                            className="rounded border-[#CBD5E1] text-[#2563EB] focus:ring-0 cursor-pointer"
                          />
                        </td>

                        {/* Organization Name & Domain */}
                        <td className="py-3.5 px-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center font-black text-[#0F172A] text-xs">
                              {org.name.slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <span className="font-extrabold text-[#0F172A] block truncate hover:text-[#2563EB]">
                                {org.name}
                              </span>
                              <span className="text-[11px] text-[#64748B] font-mono block truncate">
                                {org.domain}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td className="py-3.5 px-3 font-medium text-[#334155]">
                          <span className="inline-flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-[#94A3B8]" />
                            <span>{org.type}</span>
                          </span>
                        </td>

                        {/* Primary Market */}
                        <td className="py-3.5 px-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F1F5F9] text-[#334155] rounded-md text-[11px] font-bold font-mono">
                            <Globe className="w-3 h-3 text-[#64748B]" />
                            <span>{org.primaryMarket}</span>
                          </span>
                        </td>

                        {/* Members */}
                        <td className="py-3.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-[#64748B]" />
                            <span className="font-extrabold text-[#0F172A]">{org.membersCount}</span>
                            <span className="text-[11px] text-[#94A3B8]">users</span>
                          </div>
                        </td>

                        {/* Plan */}
                        <td className="py-3.5 px-3">
                          <span
                            className={`px-2.5 py-1 text-[11px] font-extrabold rounded-md ${
                              org.plan === 'Enterprise SLA'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : org.plan === 'Scale Plan'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : org.plan === 'Growth Plan'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-slate-100 text-slate-700 border border-slate-200'
                            }`}
                          >
                            {org.plan}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-extrabold rounded-full ${
                              org.status === 'Active'
                                ? 'bg-emerald-50 text-emerald-700'
                                : org.status === 'Suspended'
                                ? 'bg-red-50 text-red-700'
                                : org.status === 'Pending'
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-blue-50 text-blue-700'
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                org.status === 'Active'
                                  ? 'bg-emerald-500'
                                  : org.status === 'Suspended'
                                  ? 'bg-red-500'
                                  : org.status === 'Pending'
                                  ? 'bg-amber-500'
                                  : 'bg-blue-500'
                              }`}
                            />
                            {org.status}
                          </span>
                        </td>

                        {/* Created Date */}
                        <td className="py-3.5 px-3 font-mono text-[#64748B]">
                          {org.createdDate}
                        </td>

                        {/* Primary Admin */}
                        <td className="py-3.5 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-[#0F172A] text-white font-bold text-[10px] flex items-center justify-center shrink-0">
                              {org.primaryAdmin.avatarInitials}
                            </div>
                            <span className="font-mono text-[#334155] text-xs truncate max-w-[160px]">
                              {org.primaryAdmin.email}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 pr-4 pl-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setActiveOrgDetail(org)}
                            className="px-2.5 py-1 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] font-bold text-[11px] rounded-lg transition-colors cursor-pointer"
                          >
                            Details
                          </button>
                          <button
                            onClick={(e) => handleToggleStatus(org.id, e)}
                            className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                              org.status === 'Active'
                                ? 'text-[#64748B] hover:text-red-600 hover:bg-red-50'
                                : 'text-emerald-600 hover:bg-emerald-50'
                            }`}
                            title={org.status === 'Active' ? 'Suspend Organization' : 'Reactivate Organization'}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Table Pagination */}
            <div className="p-4 border-t border-[#F1F5F9] bg-[#F8FAFC]/50 flex items-center justify-between text-xs text-[#64748B]">
              <div className="flex items-center gap-2">
                <button
                  disabled
                  className="px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-xl font-bold text-[#94A3B8] cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Previous</span>
                </button>
                <div className="flex items-center gap-1 font-mono">
                  <span className="w-7 h-7 rounded-lg bg-[#2563EB] text-white font-bold flex items-center justify-center">
                    1
                  </span>
                  <button className="w-7 h-7 rounded-lg hover:bg-[#E2E8F0] text-[#334155] font-bold flex items-center justify-center cursor-pointer">
                    2
                  </button>
                  <button className="w-7 h-7 rounded-lg hover:bg-[#E2E8F0] text-[#334155] font-bold flex items-center justify-center cursor-pointer">
                    3
                  </button>
                  <span className="px-1 text-[#94A3B8]">...</span>
                  <button className="w-7 h-7 rounded-lg hover:bg-[#E2E8F0] text-[#334155] font-bold flex items-center justify-center cursor-pointer">
                    125
                  </button>
                </div>
                <button
                  onClick={() => triggerToast('Navigating to next page...')}
                  className="px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-xl font-bold text-[#0F172A] hover:bg-[#F8FAFC] cursor-pointer flex items-center gap-1"
                >
                  <span>Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="font-medium">
                Page 1 of 125 (10 orgs per page)
              </span>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* 6. SLIDE-OVER DRAWER: ORGANIZATION DEEP DETAILS                */}
      {/* =============================================================== */}
      {activeOrgDetail && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex justify-end">
          <div className="bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 overflow-hidden">
            {/* Drawer Header */}
            <div className="p-6 border-b border-[#E2E8F0] flex items-start justify-between bg-[#F8FAFC]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#0F172A] text-white font-black text-lg flex items-center justify-center shadow-xs">
                  {activeOrgDetail.name.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-black text-[#0F172A] tracking-tight">
                      {activeOrgDetail.name}
                    </h2>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-extrabold rounded-full ${
                        activeOrgDetail.status === 'Active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${activeOrgDetail.status === 'Active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      {activeOrgDetail.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#64748B] font-mono mt-0.5">
                    <span>{activeOrgDetail.domain}</span>
                    <span>•</span>
                    <span>ID: {activeOrgDetail.id}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setActiveOrgDetail(null)}
                className="p-2 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#E2E8F0] rounded-xl transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-1 px-6 border-b border-[#E2E8F0] bg-white overflow-x-auto text-xs font-bold">
              {(
                [
                  { id: 'overview', label: 'Overview', icon: Building2 },
                  { id: 'members', label: `Members (${activeOrgDetail.members.length})`, icon: Users },
                  { id: 'projects', label: `Developer Projects (${activeOrgDetail.projects.length})`, icon: FolderGit2 },
                  { id: 'apikeys', label: `API Keys (${activeOrgDetail.apiKeys.length})`, icon: Key },
                  { id: 'entitlements', label: 'Entitlements', icon: Zap },
                  { id: 'support', label: 'Support & SLA', icon: LifeBuoy },
                  { id: 'billing', label: 'Billing Plan', icon: CreditCard },
                  { id: 'audit', label: 'Audit Log', icon: History }
                ] as const
              ).map((tab) => {
                const Icon = tab.icon;
                const isActive = detailTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setDetailTab(tab.id)}
                    className={`py-3.5 px-3 border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                      isActive
                        ? 'border-[#2563EB] text-[#2563EB]'
                        : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Tab Content Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs">
              {/* TAB 1: OVERVIEW */}
              {detailTab === 'overview' && (
                <div className="space-y-6">
                  {/* Key Properties Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[10px] uppercase font-bold text-[#94A3B8] tracking-wider">Plan & Tier</span>
                      <div className="font-extrabold text-sm text-[#0F172A]">{activeOrgDetail.plan}</div>
                      <span className="text-[11px] text-[#64748B]">{activeOrgDetail.type}</span>
                    </div>

                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[10px] uppercase font-bold text-[#94A3B8] tracking-wider">Primary Market</span>
                      <div className="font-extrabold text-sm text-[#0F172A] flex items-center gap-1.5">
                        <Globe className="w-4 h-4 text-[#2563EB]" />
                        <span>{activeOrgDetail.primaryMarket}</span>
                      </div>
                      <span className="text-[11px] text-[#64748B]">Regional compliance active</span>
                    </div>

                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[10px] uppercase font-bold text-[#94A3B8] tracking-wider">Primary Admin</span>
                      <div className="font-extrabold text-xs text-[#0F172A]">{activeOrgDetail.primaryAdmin.name}</div>
                      <span className="text-[11px] font-mono text-[#64748B]">{activeOrgDetail.primaryAdmin.email}</span>
                    </div>

                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[10px] uppercase font-bold text-[#94A3B8] tracking-wider">Created / Active</span>
                      <div className="font-mono text-xs text-[#0F172A] font-bold">{activeOrgDetail.createdDate}</div>
                      <span className="text-[11px] text-[#64748B]">Last: {activeOrgDetail.lastActivity}</span>
                    </div>
                  </div>

                  {/* Enterprise Workspace Hierarchy (Real Enterprise Feature) */}
                  {activeOrgDetail.childDivisions && activeOrgDetail.childDivisions.length > 0 && (
                    <div className="p-4 bg-purple-50/50 border border-purple-200 rounded-xl space-y-2.5">
                      <div className="flex items-center gap-2 text-purple-900 font-extrabold">
                        <Building2 className="w-4 h-4 text-purple-700" />
                        <span>Enterprise Workspace Multi-Division Hierarchy</span>
                      </div>
                      <p className="text-[11px] text-purple-800 leading-relaxed">
                        This parent organization orchestrates independent workspace accounts across subsidiary business units:
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {activeOrgDetail.childDivisions.map((div) => (
                          <span
                            key={div}
                            className="px-2.5 py-1 bg-white border border-purple-200 text-purple-900 font-bold rounded-lg text-xs flex items-center gap-1 shadow-2xs"
                          >
                            <Building2 className="w-3 h-3 text-purple-600" />
                            <span>{div}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quota Gauge Snapshot */}
                  <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-2">
                    <div className="flex items-center justify-between font-bold">
                      <span className="text-[#0F172A]">Current Monthly Quota Consumption</span>
                      <span className="font-mono text-[#2563EB]">
                        {((activeOrgDetail.entitlements.monthlyApiUsed / activeOrgDetail.entitlements.monthlyApiQuota) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#2563EB] rounded-full"
                        style={{
                          width: `${(activeOrgDetail.entitlements.monthlyApiUsed / activeOrgDetail.entitlements.monthlyApiQuota) * 100}%`
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-[#64748B] font-mono">
                      <span>{activeOrgDetail.entitlements.monthlyApiUsed.toLocaleString()} calls</span>
                      <span>Cap: {activeOrgDetail.entitlements.monthlyApiQuota.toLocaleString()} calls/mo</span>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: MEMBERS */}
              {detailTab === 'members' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-[#0F172A]">Organization Team Roster</span>
                    <button
                      onClick={() => triggerToast(`Invited new member to ${activeOrgDetail.name}`)}
                      className="px-3 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      <span>Invite Member</span>
                    </button>
                  </div>

                  <div className="border border-[#E2E8F0] rounded-xl divide-y divide-[#F1F5F9] overflow-hidden">
                    {activeOrgDetail.members.map((member) => (
                      <div key={member.id} className="p-3.5 flex items-center justify-between hover:bg-[#F8FAFC]">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white font-bold text-xs flex items-center justify-center">
                            {member.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-extrabold text-[#0F172A]">{member.name}</span>
                              {member.mfaEnabled && (
                                <span className="px-1.5 py-0.2 bg-emerald-50 text-emerald-700 text-[9px] font-bold rounded border border-emerald-200">
                                  MFA
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-[#64748B] font-mono block">{member.email}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#334155] font-bold rounded-md text-[11px]">
                            {member.role}
                          </span>
                          <span className="text-[11px] text-[#94A3B8]">{member.lastActive}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 3: DEVELOPER PROJECTS */}
              {detailTab === 'projects' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-[#0F172A]">Client Developer Projects</span>
                    <button
                      onClick={() => triggerToast(`Project creation wizard triggered for ${activeOrgDetail.name}`)}
                      className="px-3 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>New Project</span>
                    </button>
                  </div>

                  {activeOrgDetail.projects.length > 0 ? (
                    <div className="space-y-3">
                      {activeOrgDetail.projects.map((proj) => (
                        <div key={proj.id} className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-2.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FolderGit2 className="w-4 h-4 text-[#2563EB]" />
                              <span className="font-extrabold text-sm text-[#0F172A]">{proj.name}</span>
                            </div>
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-[10px] rounded border border-emerald-200">
                              {proj.environment}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            {proj.services.map((srv) => (
                              <span key={srv} className="px-2 py-0.5 bg-white border border-[#CBD5E1] rounded text-[10px] font-bold text-[#334155]">
                                {srv}
                              </span>
                            ))}
                          </div>

                          <div className="pt-2 border-t border-[#E2E8F0] flex items-center justify-between text-[11px]">
                            <span className="font-mono text-[#64748B] truncate max-w-[280px]">
                              Webhook: {proj.webhookUrl}
                            </span>
                            <span className="font-bold text-[#0F172A] font-mono">
                              {proj.monthlyCalls.toLocaleString()} reqs this month
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-[#64748B]">
                      No active developer projects registered yet.
                    </div>
                  )}
                </div>
              )}

              {/* TAB 4: API KEYS METADATA (Strictly Masked) */}
              {detailTab === 'apikeys' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-extrabold text-[#0F172A] block">API Credentials Metadata</span>
                      <span className="text-[11px] text-[#64748B]">Secret tokens are strictly hashed & masked.</span>
                    </div>
                    <button
                      onClick={() => triggerToast(`Generated new scoped API key for ${activeOrgDetail.name}`)}
                      className="px-3 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Generate Key</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {activeOrgDetail.apiKeys.map((k) => (
                      <div key={k.id} className="p-4 bg-white border border-[#E2E8F0] rounded-xl space-y-3 shadow-2xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Key className="w-4 h-4 text-[#2563EB]" />
                            <span className="font-extrabold text-xs text-[#0F172A]">{k.name}</span>
                          </div>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-bold text-[10px] rounded">
                            {k.environment}
                          </span>
                        </div>

                        <div className="p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg font-mono text-xs text-[#0F172A] flex items-center justify-between">
                          <span>{k.maskedKey}</span>
                          <span className="text-[10px] text-[#64748B]">Rate Limit: {k.rateLimit}</span>
                        </div>

                        <div className="flex items-center justify-between text-[11px] pt-1">
                          <span className="text-[#64748B]">Last used: <strong className="text-[#0F172A]">{k.lastUsed}</strong></span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => triggerToast(`Rolled key ${k.name}. 24h grace period started.`)}
                              className="text-[#2563EB] hover:underline font-bold cursor-pointer"
                            >
                              Roll Key
                            </button>
                            <span>•</span>
                            <button
                              onClick={() => triggerToast(`Revoked key ${k.name}`)}
                              className="text-red-600 hover:underline font-bold cursor-pointer"
                            >
                              Revoke
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 5: ENTITLEMENTS */}
              {detailTab === 'entitlements' && (
                <div className="space-y-4">
                  <span className="font-extrabold text-[#0F172A] block">Platform Entitlements & Engine Quotas</span>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[#64748B] font-bold text-[11px]">Monthly API Call Quota</span>
                      <div className="text-base font-black text-[#0F172A] font-mono">
                        {activeOrgDetail.entitlements.monthlyApiQuota.toLocaleString()}
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">Hard-enforced cap</span>
                    </div>

                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[#64748B] font-bold text-[11px]">Concurrency Allocation</span>
                      <div className="text-base font-black text-[#0F172A] font-mono">
                        {activeOrgDetail.entitlements.calculationConcurrencyLimit} workers
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">Parallel execution threads</span>
                    </div>

                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[#64748B] font-bold text-[11px]">Custom Formula Injection</span>
                      <div className="text-sm font-black text-[#0F172A]">
                        {activeOrgDetail.entitlements.customTaxFormulasEnabled ? '✅ Enabled' : '❌ Disabled'}
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">Custom salary/tax engine</span>
                    </div>

                    <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                      <span className="text-[#64748B] font-bold text-[11px]">Webhook Retention Window</span>
                      <div className="text-base font-black text-[#0F172A] font-mono">
                        {activeOrgDetail.entitlements.webhookRetentionHours} hours
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">Automatic retry buffer</span>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 6: SUPPORT & SLA */}
              {detailTab === 'support' && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50/60 border border-blue-200 rounded-xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-extrabold text-blue-900 text-sm">Designated Account TAM</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-bold text-[10px] rounded">
                        {activeOrgDetail.support.slaResponseTime}
                      </span>
                    </div>
                    <div className="text-xs text-blue-800 font-medium">
                      <strong>{activeOrgDetail.support.tamName}</strong> ({activeOrgDetail.support.tamEmail})
                    </div>
                  </div>

                  <span className="font-extrabold text-[#0F172A] block pt-2">Linked Support Tickets</span>
                  {activeOrgDetail.tickets.length > 0 ? (
                    <div className="space-y-2.5">
                      {activeOrgDetail.tickets.map((tkt) => (
                        <div key={tkt.id} className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-[#0F172A] text-xs">{tkt.title}</span>
                            <span className="px-2 py-0.5 bg-white border border-[#CBD5E1] text-[#334155] font-bold text-[10px] rounded">
                              {tkt.status}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-[#64748B]">
                            <span>ID: {tkt.id} • {tkt.priority}</span>
                            <span>Updated: {tkt.lastUpdated}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-[#64748B]">
                      No active support tickets for this organization.
                    </div>
                  )}
                </div>
              )}

              {/* TAB 7: BILLING (Client Subscription Details ONLY - No Fake Merchant Volume) */}
              {detailTab === 'billing' && (
                <div className="space-y-4">
                  <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-extrabold text-sm text-[#0F172A]">Platform License Subscription</span>
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-[10px] rounded border border-emerald-200">
                        {activeOrgDetail.billing.status}
                      </span>
                    </div>

                    <div className="text-2xl font-black text-[#0F172A]">
                      ${activeOrgDetail.billing.monthlyCost.toLocaleString()}
                      <span className="text-xs font-normal text-[#64748B]"> / month ({activeOrgDetail.billing.billingCycle})</span>
                    </div>

                    <div className="pt-2 border-t border-[#E2E8F0] space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-[#64748B]">Invoicing Email:</span>
                        <span className="font-mono text-[#0F172A] font-bold">{activeOrgDetail.billing.invoiceEmail}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#64748B]">Payment Method:</span>
                        <span className="text-[#0F172A] font-medium">{activeOrgDetail.billing.paymentMethod}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#64748B]">Next Renewal Date:</span>
                        <span className="font-mono text-[#0F172A]">{activeOrgDetail.billing.nextInvoiceDate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 8: AUDIT LOG */}
              {detailTab === 'audit' && (
                <div className="space-y-3">
                  <span className="font-extrabold text-[#0F172A] block">Workspace Administrative Audit Trail</span>
                  {activeOrgDetail.auditLogs.length > 0 ? (
                    <div className="space-y-2">
                      {activeOrgDetail.auditLogs.map((log) => (
                        <div key={log.id} className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="font-bold text-[#0F172A]">{log.action}</span>
                            <span className="font-mono text-[#94A3B8]">{log.timestamp}</span>
                          </div>
                          <div className="text-[11px] text-[#64748B] flex items-center justify-between">
                            <span>Actor: <strong className="text-[#334155]">{log.actor}</strong></span>
                            <span className="font-mono text-[10px]">{log.ipAddress}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-[#64748B]">
                      No audit log entries recorded in the last 30 days.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Drawer Footer Actions */}
            <div className="p-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-between">
              <button
                onClick={(e) => handleToggleStatus(activeOrgDetail.id, e)}
                className={`px-3.5 py-2 font-bold text-xs rounded-xl transition-colors cursor-pointer ${
                  activeOrgDetail.status === 'Active'
                    ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                }`}
              >
                {activeOrgDetail.status === 'Active' ? 'Suspend Organization' : 'Reactivate Organization'}
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveOrgDetail(null)}
                  className="px-4 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#334155] font-bold text-xs rounded-xl cursor-pointer"
                >
                  Close
                </button>
                <button
                  onClick={() => triggerToast(`Organization settings saved for ${activeOrgDetail.name}`)}
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 7. MODAL: ADD NEW CLIENT ORGANIZATION                           */}
      {/* =============================================================== */}
      {showAddOrgModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#2563EB]" />
                <h3 className="text-base font-bold text-[#0F172A]">Add Client Organization</h3>
              </div>
              <button
                onClick={() => setShowAddOrgModal(false)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateOrganization} className="space-y-4 text-xs">
              <div>
                <label className="font-extrabold text-[#0F172A] block mb-1">Organization Name *</label>
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs focus:ring-2 focus:ring-[#2563EB] outline-none"
                  placeholder="e.g. Acme Corp Global"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-extrabold text-[#0F172A] block mb-1">Domain *</label>
                  <input
                    type="text"
                    value={newOrgDomain}
                    onChange={(e) => setNewOrgDomain(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl font-mono text-xs focus:ring-2 focus:ring-[#2563EB] outline-none"
                    placeholder="e.g. acme.com"
                    required
                  />
                </div>

                <div>
                  <label className="font-extrabold text-[#0F172A] block mb-1">Primary Market</label>
                  <select
                    value={newOrgMarket}
                    onChange={(e) => setNewOrgMarket(e.target.value as PrimaryMarket)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs focus:ring-2 focus:ring-[#2563EB] outline-none font-bold text-[#334155]"
                  >
                    <option value="Global">Global</option>
                    <option value="US">US</option>
                    <option value="EU (Eurozone)">EU (Eurozone)</option>
                    <option value="UK (GBP)">UK (GBP)</option>
                    <option value="APAC">APAC</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-extrabold text-[#0F172A] block mb-1">Organization Type</label>
                  <select
                    value={newOrgType}
                    onChange={(e) => setNewOrgType(e.target.value as OrgType)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs focus:ring-2 focus:ring-[#2563EB] outline-none font-bold text-[#334155]"
                  >
                    <option value="Enterprise">Enterprise</option>
                    <option value="Developer Platform">Developer Platform</option>
                    <option value="Scale Workspace">Scale Workspace</option>
                    <option value="Team / Startup">Team / Startup</option>
                  </select>
                </div>

                <div>
                  <label className="font-extrabold text-[#0F172A] block mb-1">API Plan / Tier</label>
                  <select
                    value={newOrgPlan}
                    onChange={(e) => setNewOrgPlan(e.target.value as OrgPlan)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs focus:ring-2 focus:ring-[#2563EB] outline-none font-bold text-[#334155]"
                  >
                    <option value="Enterprise SLA">Enterprise SLA (10M req/mo)</option>
                    <option value="Scale Plan">Scale Plan (2.5M req/mo)</option>
                    <option value="Growth Plan">Growth Plan (1M req/mo)</option>
                    <option value="Free Developer">Free Developer (50k req/mo)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1 border-t border-[#F1F5F9]">
                <div>
                  <label className="font-extrabold text-[#0F172A] block mb-1">Primary Admin Name</label>
                  <input
                    type="text"
                    value={newOrgAdminName}
                    onChange={(e) => setNewOrgAdminName(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs focus:ring-2 focus:ring-[#2563EB] outline-none"
                    placeholder="e.g. Sarah Jenkins"
                  />
                </div>

                <div>
                  <label className="font-extrabold text-[#0F172A] block mb-1">Primary Admin Email *</label>
                  <input
                    type="email"
                    value={newOrgAdminEmail}
                    onChange={(e) => setNewOrgAdminEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl font-mono text-xs focus:ring-2 focus:ring-[#2563EB] outline-none"
                    placeholder="e.g. sarah@acme.com"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => setShowAddOrgModal(false)}
                  className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] font-bold text-xs rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Create Organization</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
